<script>
  import ContainerNode from './ContainerNode.svelte';
  import ChevronRightIcon from './ChevronRightIcon.svelte';
  import EditIcon from './EditIcon/index.svelte';
  import ContainerDetails from './ContainerDetails.svelte';
  import StatusBadge from './StatusBadge.svelte';
  import DeleteConfirmation from './DeleteConfirmation.svelte';
  import { router } from '@inertiajs/svelte';

  let { container, containers, canManageContainers, level = 0, isExpanded, toggleContainer, onMove = null } = $props();
  let showDeleteConfirm = $state(false);
  let deleteItems = $state(false);
  let deleteChildren = $state(false);

  function formatContainerType(type) {
    return type.charAt(0).toUpperCase() + type.slice(1);
  }

  function handleDeleteClick() {
    showDeleteConfirm = true;
  }

  function handleDeleteConfirm({ checkboxChecked }) {
    router.delete(`/containers/${container.id}`, {
      data: {
        delete_items: checkboxChecked,
        delete_children: deleteChildren
      },
      onSuccess: () => {
        // Visit the current page to refresh the data
        router.visit(window.location.pathname, { preserveScroll: true });
      }
    });
  }

  function handleDeleteCancel() {
    showDeleteConfirm = false;
    deleteItems = false;
    deleteChildren = false;
  }

  // Child containers, reactive: more may arrive as pages load via scroll
  const childContainers = $derived(containers.filter(c => c.parent_container_id === container.id));
  const hasChildren = $derived(childContainers.length > 0);
</script>

<div class="group border border-gray-200 rounded-xl bg-white hover:border-brand-600 hover:shadow-md transition-all {level > 0 ? 'mt-2' : ''}">
  <div class="p-3.5 flex items-center gap-3">
    <!-- Expand toggle / spacer -->
    {#if hasChildren}
      <button
        class="flex-shrink-0 text-gray-400 hover:text-brand-700 focus:outline-none"
        onclick={() => toggleContainer(container.id)}
        aria-label={isExpanded(container.id) ? `Collapse ${container.label}` : `Expand ${container.label}`}
      >
        <div class="h-5 w-5 transform transition-transform duration-200 {isExpanded(container.id) ? 'rotate-90' : ''}">
          <ChevronRightIcon />
        </div>
      </button>
    {:else}
      <div class="w-5 flex-shrink-0"></div>
    {/if}

    <!-- Thumbnail or icon -->
    {#if container.image_url}
      <img
        src={container.image_url}
        alt={container.label}
        class="h-10 w-10 flex-shrink-0 object-cover rounded-lg"
      />
    {:else}
      <div class="h-10 w-10 flex-shrink-0 bg-brand-50 text-brand-700 rounded-lg grid place-items-center">
        <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path d="M21 8l-9-5-9 5v8l9 5 9-5V8z"/><path d="M3 8l9 5 9-5"/>
        </svg>
      </div>
    {/if}

    <!-- Identity + condensed meta -->
    <div class="flex-1 min-w-0">
      <div class="flex items-center gap-2 flex-wrap">
        <a href={`/containers/${container.id}`} class="text-sm font-semibold text-gray-900 hover:text-brand-700 truncate">
          {container.label}
        </a>
        <span class="text-[11px] font-semibold text-gray-500 bg-gray-100 rounded-full px-2 py-0.5">
          {formatContainerType(container.container_type)}
        </span>
      </div>
      <ContainerDetails {container} />
      <div class="flex items-center gap-2.5 mt-1.5">
        <StatusBadge value={container.status} />
        {#if container.is_valuable}
          <svg class="h-3.5 w-3.5 text-amber-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-label="Valuable"><title>Valuable</title><path d="M6 3h12l3 6-9 12L3 9l3-6z"/><path d="M3 9h18"/><path d="M9 3l3 6 3-6"/><path d="M12 9v12"/></svg>
        {/if}
        {#if container.is_favorite}
          <svg class="h-3.5 w-3.5 text-red-500" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-label="Favorite"><title>Favorite</title><path d="M12 20s-7-4.5-9.5-9A5 5 0 0112 5a5 5 0 019.5 6c-2.5 4.5-9.5 9-9.5 9z"/></svg>
        {/if}
        <span class="text-xs text-gray-400">
          {container.item_count} {container.item_count === 1 ? 'item' : 'items'}
        </span>
        {#if hasChildren}
          <span class="text-xs text-gray-400">
            {childContainers.length} {childContainers.length === 1 ? 'child' : 'children'}
          </span>
        {/if}
      </div>
    </div>

    <!-- Actions (visible on hover/focus, always on touch) -->
    {#if canManageContainers}
      <div class="flex items-center gap-1 flex-shrink-0 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100 transition-opacity">
        {#if onMove}
          <button
            onclick={() => onMove(container)}
            class="p-2 rounded-lg border border-gray-200 bg-white text-gray-400 hover:text-brand-700 hover:border-brand-600 transition-colors"
            title={`Move ${container.label}`}
            aria-label={`Move ${container.label}`}
          >
            <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 17h9a4 4 0 004-4V7"/><path d="M14 7l3-3 3 3"/></svg>
          </button>
        {/if}
        <EditIcon
          href={`/containers/${container.id}/edit`}
          label={`Edit ${container.label}`}
          className="p-2 rounded-lg border border-gray-200 bg-white hover:text-brand-700 hover:border-brand-600"
        />
        <button
          onclick={handleDeleteClick}
          class="p-2 rounded-lg border border-gray-200 bg-white text-gray-400 hover:text-red-600 hover:border-red-400 hover:bg-red-50 transition-colors"
          title="Delete container"
          aria-label={`Delete ${container.label}`}
        >
          <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </div>
    {/if}
  </div>

  <!-- Child containers -->
  {#if hasChildren && isExpanded(container.id)}
    <div class="mx-3.5 mb-3.5 pl-4 border-l-2 border-brand-100">
      {#each childContainers as childContainer}
        <ContainerNode
          container={childContainer}
          {containers}
          {canManageContainers}
          {isExpanded}
          {toggleContainer}
          {onMove}
          level={level + 1}
        />
      {/each}
    </div>
  {/if}
</div>

<DeleteConfirmation
  show={showDeleteConfirm}
  title="Delete Container"
  message="Are you sure you want to delete this container? This action cannot be undone."
  checkboxLabel="Delete all items in this container"
  bind:checkboxChecked={deleteItems}
  onConfirm={handleDeleteConfirm}
  onCancel={handleDeleteCancel}
>
  {#snippet additionalOptions()}
  <div class="flex items-center">
    <label class="flex items-center">
      <input
        type="checkbox"
        bind:checked={deleteChildren}
        class="h-4 w-4 rounded border-gray-300 text-brand-700 focus:ring-brand-600"
      />
      <span class="ml-2 text-sm text-gray-700">Delete all child containers and their items</span>
    </label>
  </div>
  {/snippet}
</DeleteConfirmation>
